/**
 * Date/time formatting (design-system.md §9). Africa/Lagos (WAT) is the default zone —
 * WAT has no DST, so the label is a fixed literal rather than a locale-derived "GMT+1".
 *
 *   formatDate(ts)                     // "14 Feb 2026"
 *   formatDate(ts, { withTime: true }) // "14 Feb 2026, 09:32 WAT"
 *   formatDate(ts, { withSeconds: true }) // "14 Feb 2026, 09:32:07 WAT" (audit trail, F-15)
 *   formatRelative(ts)                 // "3 hours ago"
 */

const TZ = 'Africa/Lagos';

const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: TZ,
});
const TIME_FMT = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: TZ,
});
const TIME_SECONDS_FMT = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: TZ,
});
const RELATIVE_FMT = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** Accepts an ISO string, epoch-ms number, or Date. Invalid input → null. */
function toDate(ts: string | number | Date): Date | null {
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface DateOptions {
  withTime?: boolean;
  /** Time to the second (implies `withTime`) — for audit rows ordered within a minute. */
  withSeconds?: boolean;
}

export function formatDate(ts: string | number | Date, options: DateOptions = {}): string {
  const d = toDate(ts);
  if (!d) return '—';
  const date = DATE_FMT.format(d);
  if (options.withSeconds) return `${date}, ${TIME_SECONDS_FMT.format(d)} WAT`;
  return options.withTime ? `${date}, ${TIME_FMT.format(d)} WAT` : date;
}

const UNITS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['week', 60 * 60 * 24 * 7],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
];

/** "3 hours ago" / "in 2 days". `now` is injectable for deterministic tests. */
export function formatRelative(ts: string | number | Date, now: number = Date.now()): string {
  const d = toDate(ts);
  if (!d) return '—';
  const diffSeconds = Math.round((d.getTime() - now) / 1000);
  const abs = Math.abs(diffSeconds);
  for (const [unit, seconds] of UNITS) {
    if (abs >= seconds || unit === 'second') {
      return RELATIVE_FMT.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return RELATIVE_FMT.format(0, 'second');
}
